import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { DashboardKpiResponse } from "@/actions/ssr/dashboard/getDashboardKpiSSR";
import type { QuickActionsResponse } from "@/actions/ssr/dashboard/getQuickActionsSSR";
import type { ActivityFeedResponse } from "@/actions/ssr/dashboard/getActivityFeedSSR";
import type { HolidaysCalendarResponse } from "@/actions/ssr/leave/getHolidaysCalendarSSR";

interface DashboardState {
  dashboardReady: boolean;
  kpi: DashboardKpiResponse;
  quickActions: QuickActionsResponse;
  activityFeed: ActivityFeedResponse;
  calendarHolidays: HolidaysCalendarResponse;
  globalDateFilter: { from: string | null; to: string | null };
  kpiClientFetched: boolean;
  quickActionsClientFetched: boolean;
  activityFeedClientFetched: boolean;
}

const initialState: DashboardState = {
  dashboardReady: false,
  kpi: {
    total_staff: { value: 0 },
    daily_hours: { value: null },
    weekly_hours: { value: null },
    overtime_hours: { value: null },
  },
  quickActions: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    results: [],
  },
  activityFeed: { today: [], yesterday: [] },
  calendarHolidays: {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    results: [],
  },
  globalDateFilter: { from: null, to: null },
  kpiClientFetched: false,
  quickActionsClientFetched: false,
  activityFeedClientFetched: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setDashboardReady(state) {
      state.dashboardReady = true;
    },
    setDashboardKpi(state, action: PayloadAction<DashboardKpiResponse>) {
      state.kpiClientFetched = true;
      state.kpi = action.payload;
    },
    setDashboardKpiFromSSR(state, action: PayloadAction<DashboardKpiResponse>) {
      if (state.kpiClientFetched) return;
      state.kpi = action.payload;
    },
    setQuickActions(state, action: PayloadAction<QuickActionsResponse>) {
      state.quickActionsClientFetched = true;
      state.quickActions = action.payload;
    },
    setQuickActionsFromSSR(state, action: PayloadAction<QuickActionsResponse>) {
      if (state.quickActionsClientFetched) return;
      state.quickActions = action.payload;
    },
    removeQuickAction(state, action: PayloadAction<string>) {
      state.quickActions.results = state.quickActions.results.filter(
        (item) => item.id !== action.payload,
      );
      state.quickActions.count = Math.max(0, state.quickActions.count - 1);
    },
    setActivityFeed(state, action: PayloadAction<ActivityFeedResponse>) {
      state.activityFeedClientFetched = true;
      state.activityFeed = action.payload;
    },
    setActivityFeedFromSSR(state, action: PayloadAction<ActivityFeedResponse>) {
      if (state.activityFeedClientFetched) return;
      state.activityFeed = action.payload;
    },
    setCalendarHolidays(
      state,
      action: PayloadAction<HolidaysCalendarResponse>,
    ) {
      state.calendarHolidays = action.payload;
    },
    setGlobalDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.globalDateFilter = action.payload;
    },
  },
});

export const {
  setDashboardReady,
  setDashboardKpi,
  setDashboardKpiFromSSR,
  setQuickActions,
  setQuickActionsFromSSR,
  removeQuickAction,
  setActivityFeed,
  setActivityFeedFromSSR,
  setCalendarHolidays,
  setGlobalDateFilter,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
